var m = require('mithril');
var prop = require('mithril/stream');
var flash = require('../../components/flash');
var buttonElement = require('../../components/button');
var treeLocations = require('../../components/treeLocations');
var toggleSwitch = require('../../components/toggleSwitch');
var modal = require('../../layout/components/modal');
var loaders = require('../../layout/components/loaders');
var columnForm = require('../../data/components/columnModal');
var fieldnetTree = require('../../network/components/fieldnetTree');
var eventBindings = require('../../../../../models/reports/eventBindings');

var EVENT_BINDING_DATASET_PURPOSE = eventBindings.EVENT_BINDING_DATASET_PURPOSE;
var EVENT_BINDING_DATASET_NAME = eventBindings.EVENT_BINDING_DATASET_NAME;
var EVENT_BINDING_COLUMN_NAME = eventBindings.EVENT_BINDING_COLUMN_NAME;
var normalizeRid = eventBindings.normalizeRid;

function getEventConfig(record) {
  var eventConfig = {};
  if (record && record.eventConfig && typeof record.eventConfig === 'string') {
    try {
      eventConfig = JSON.parse(record.eventConfig);
    } catch (e) {
      eventConfig = {};
    }
  } else if (record && record.eventConfig && typeof record.eventConfig === 'object') {
    eventConfig = record.eventConfig;
  }
  var bindings = Array.isArray(eventConfig.bindings) ? eventConfig.bindings : [];

  return {
    enabled: Boolean(eventConfig.enabled),
    clearPolicy: eventConfig.clearPolicy || 'all-normal',
    bindings: bindings
      .filter(function (binding) {
        return binding && binding.columnRid && binding.role;
      })
      .map(function (binding) {
        return {
          columnRid: normalizeRid(binding.columnRid),
          pointRid: normalizeRid(binding.pointRid),
          datasetRid: normalizeRid(binding.datasetRid),
          columnId: binding.columnId,
          role: binding.role
        };
      })
  };
}

function formatLocationSegment(node) {
  if (!node || typeof node !== 'object') return '';
  if (node.name === 'root') return 'Home';
  if (node['@class'] === 'Room' && node.name && node.roomNumber) {
    return node.name + ' ' + node.roomNumber;
  }
  return node.name || node.roomNumber || node['@class'] || '';
}

function normalizeLocationPath(path) {
  var nodes = Array.isArray(path) ? path.slice() : [];
  if (nodes.length <= 1) return nodes;

  var first = nodes[0] || {};
  var last = nodes[nodes.length - 1] || {};
  var firstLooksRoot = first.name === 'root' || first.name === 'Home' || first['@class'] === 'Dashboard';
  var lastLooksRoot = last.name === 'root' || last.name === 'Home' || last['@class'] === 'Dashboard';

  if (!firstLooksRoot && lastLooksRoot) {
    nodes.reverse();
  }

  return nodes;
}

function buildLocationPathLabel(locationRecord) {
  var nodes = normalizeLocationPath(locationRecord && locationRecord.path);
  if (nodes.length === 0 && locationRecord) {
    nodes = [locationRecord];
  }
  return nodes
    .map(formatLocationSegment)
    .filter(Boolean)
    .join(' / ');
}

function buildPointPathLabel(resourceRecord, pointName, pointRid) {
  var breadcrumbs = Array.isArray(resourceRecord && resourceRecord.breadcrumbs) ? resourceRecord.breadcrumbs : [];
  var segments = breadcrumbs
    .map(function (node) {
      return node && (node.name || node['@class']);
    })
    .filter(Boolean);

  if (pointName) {
    segments.push(pointName);
  } else if (pointRid) {
    segments.push(pointRid);
  }

  return segments.join(' / ');
}

function ensurePointPathLabel(ctrl, pointRid, pointName) {
  var normalizedRid = normalizeRid(pointRid);
  if (!normalizedRid) {
    return pointName || 'Unnamed point';
  }

  var cached = ctrl.eventPointPathLabels[normalizedRid];
  if (cached) {
    if (cached.status === 'loaded' && cached.label) {
      return cached.label;
    }
    if (cached.status === 'loading') {
      return pointName || 'Loading…';
    }
  }

  ctrl.eventPointPathLabels[normalizedRid] = {
    status: 'loading',
    label: pointName || ''
  };

  loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/point/' + normalizedRid.slice(1) + '/resource',
    method: 'GET',
    background: true
  })
  .then(function (resourceRecord) {
    ctrl.eventPointPathLabels[normalizedRid] = {
      status: 'loaded',
      label: buildPointPathLabel(resourceRecord, pointName, normalizedRid)
    };
    m.redraw();
  })
  .catch(function () {
    ctrl.eventPointPathLabels[normalizedRid] = {
      status: 'loaded',
      label: pointName || normalizedRid
    };
    m.redraw();
  });

  return pointName || 'Loading…';
}

function loadLocationSummary(ctrl, rid) {
  var normalizedRid = normalizeRid(rid);

  if (!normalizedRid) {
    ctrl.eventLocationRid = null;
    ctrl.eventLocationPathLabel = '';
    ctrl.eventLocationLoading = false;
    return Promise.resolve(null);
  }

  if (ctrl.eventLocationRid === normalizedRid && ctrl.eventLocationPathLabel) {
    return Promise.resolve(ctrl.eventLocationPathLabel);
  }

  ctrl.eventLocationRid = normalizedRid;
  ctrl.eventLocationLoading = true;

  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/site/' + normalizedRid.slice(1),
    method: 'GET',
    background: true
  })
  .then(function (locationRecord) {
    if (ctrl.eventLocationRid === normalizedRid) {
      ctrl.eventLocationPathLabel = buildLocationPathLabel(locationRecord) || normalizedRid;
      ctrl.eventLocationLoading = false;
      m.redraw();
    }
    return locationRecord;
  })
  .catch(function () {
    if (ctrl.eventLocationRid === normalizedRid) {
      ctrl.eventLocationPathLabel = normalizedRid;
      ctrl.eventLocationLoading = false;
      m.redraw();
    }
    return null;
  });
}

function buildStateSignature(record) {
  return JSON.stringify({
    locationRid: normalizeRid(record && record.locationRid),
    eventConfig: getEventConfig(record)
  });
}

function queueSaveEventConfig(ctrl) {
  ctrl._eventSaveSequence = (ctrl._eventSaveSequence || Promise.resolve())
    .catch(function () {})
    .then(function () {
      return saveEventConfig(ctrl, ctrl._activeRecord);
    });

  return ctrl._eventSaveSequence;
}

function createEventState(ctrl, record) {
  var eventConfig = getEventConfig(record);
  var initialLocation = record && record.locationRid ? { '@rid': normalizeRid(record.locationRid) } : {};
  if (ctrl.showLocation !== false) {
    loadLocationSummary(ctrl, initialLocation && initialLocation['@rid']);
  }

  return {
    enabled: eventConfig.enabled,
    clearPolicy: eventConfig.clearPolicy,
    locationSelection: prop(initialLocation)
  };
}

function syncEventState(ctrl, record) {
  var rid = record && record['@rid'];
  var signature = buildStateSignature(record);
  var ridChanged = ctrl._eventStateRid !== rid;

  if (ridChanged || (!ctrl.eventStateDirty && ctrl._eventStateSignature !== signature)) {
    ctrl.eventState = createEventState(ctrl, record || {});
    ctrl.eventErrors = [];
    ctrl.eventSaveInFlight = false;
    ctrl.eventStateDirty = false;
    ctrl._eventStateRid = rid;
    ctrl._eventStateSignature = signature;
  }
}

function getSelectedLocationRid(ctrl, record) {
  var selected = ctrl.eventState && ctrl.eventState.locationSelection ? ctrl.eventState.locationSelection() : null;
  return normalizeRid((selected && selected['@rid']) || (record && record.locationRid));
}

function setDirty(ctrl) {
  ctrl.eventStateDirty = true;
}

function buildBindingsPayload(record) {
  return eventBindings.buildEventConfigBindings(record);
}

function bindingSignature(bindings) {
  return JSON.stringify((Array.isArray(bindings) ? bindings : [])
    .map(function (binding) {
      return {
        role: binding.role,
        columnRid: normalizeRid(binding.columnRid),
        pointRid: normalizeRid(binding.pointRid),
        datasetRid: normalizeRid(binding.datasetRid)
      };
    })
    .sort(function (a, b) {
      return (a.role + a.columnRid).localeCompare(b.role + b.columnRid);
    }));
}

function validateEventConfig(ctrl, record) {
  var errors = [];
  var bindings = buildBindingsPayload(record);

  if (ctrl.eventState.enabled) {
    if (!bindings.length) {
      errors.push('Add at least one event point before turning event tracking on.');
    }
    if (!getSelectedLocationRid(ctrl, record)) {
      errors.push('Choose a location before turning event tracking on.');
    }
  }

  return errors;
}

function saveEventConfig(ctrl, record) {
  if (!record || !record['@rid']) {
    return Promise.resolve(null);
  }

  var bindings = buildBindingsPayload(record);
  var locationRid = getSelectedLocationRid(ctrl, record);
  var errors = validateEventConfig(ctrl, record);

  ctrl.eventErrors = errors;
  if (errors.length > 0) {
    return Promise.reject(errors);
  }

  ctrl.eventSaveInFlight = true;

  return loaders.requestWithLoader({
    loader: 'submit',
    url: '/api/v1/report/' + record['@rid'].slice(1),
    method: 'PUT',
    body: {
      locationRid: locationRid,
      eventConfig: {
        enabled: Boolean(ctrl.eventState.enabled),
        clearPolicy: ctrl.eventState.clearPolicy || 'all-normal',
        bindings: bindings
      }
    }
  })
  .then(function (result) {
    record.locationRid = locationRid;
    record.eventConfig = {
      enabled: Boolean(ctrl.eventState.enabled),
      clearPolicy: ctrl.eventState.clearPolicy || 'all-normal',
      bindings: ctrl.eventState.enabled ? bindings : []
    };
    ctrl._eventStateSignature = buildStateSignature(record);
    ctrl._bindingSyncAttemptSignature = null;
    ctrl.eventStateDirty = false;
    return result;
  })
  .catch(function (error) {
    if (!Array.isArray(error)) {
      if (error && typeof error === 'object' && error.error) {
        ctrl.eventErrors = [error.error];
      } else if (error && error.message) {
        ctrl.eventErrors = [error.message];
      } else if (typeof error === 'string') {
        ctrl.eventErrors = [error];
      }
    }
    return Promise.reject(error);
  })
  .finally(function () {
    ctrl.eventSaveInFlight = false;
    m.redraw();
  });
}

function ensureEventBindingDataset(record) {
  var existingDataset = eventBindings.getEventBindingDataset(record);
  if (existingDataset && existingDataset['@rid']) {
    return Promise.resolve(existingDataset);
  }

  return loaders.requestWithLoader({
    loader: 'submit',
    url: '/api/v1/dataset',
    method: 'POST',
    body: {
      parent: record['@rid'],
      name: EVENT_BINDING_DATASET_NAME,
      purpose: EVENT_BINDING_DATASET_PURPOSE,
      visible: 'false',
      isSystem: true,
      type: 'list'
    }
  })
  .then(function (datasetRecord) {
    if (!Array.isArray(record.out_Contains)) {
      record.out_Contains = [];
    }
    if (!eventBindings.getEventBindingDataset(record)) {
      record.out_Contains.push({
        in: datasetRecord
      });
    }
    return datasetRecord;
  });
}

function refreshReportRecord(record) {
  if (!record || !record['@rid']) {
    return Promise.resolve(record);
  }

  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/report/' + record['@rid'].slice(1),
    method: 'GET',
    background: true
  })
  .then(function (freshRecord) {
    Object.keys(record).forEach(function (key) {
      delete record[key];
    });
    Object.assign(record, freshRecord);
    return record;
  });
}

function openEventPointPicker(ctrl, record, datasetRecord, cell) {
  var selectedPoint = prop(normalizeRid(cell && cell.pointRid));

  function submit() {
    var pointRid = normalizeRid(selectedPoint());

    if (!pointRid) {
      ctrl.eventErrors = ['Choose a point before saving.'];
      m.redraw();
      return Promise.reject(new Error('Point selection is required.'));
    }

    ctrl.eventErrors = [];

    return loaders.requestWithLoader({
      loader: 'submit',
      url: cell && cell.columnRid
        ? '/api/v1/column/' + normalizeRid(cell.columnRid).slice(1)
        : '/api/v1/column',
      method: cell && cell.columnRid ? 'PUT' : 'POST',
      body: {
        out: datasetRecord['@rid'],
        in: pointRid,
        name: EVENT_BINDING_COLUMN_NAME,
        index: cell && typeof cell.columnId !== 'undefined'
          ? cell.columnId
          : eventBindings.getNextEventBindingIndex(record)
      }
    })
    .then(function () {
      return refreshReportRecord(record);
    })
    .then(function () {
      modal.vm.close();
      m.redraw();
    })
    .catch(function (error) {
      ctrl.eventErrors = [error && error.error ? error.error : (error && error.message ? error.message : 'Could not save the event point.')];
      m.redraw();
      return Promise.reject(error);
    });
  }

  modal.vm.title(cell && cell.columnRid ? 'Edit Event Point' : 'Add Event Point');
  modal.vm.body = function () {
    return m('form.form-dark', { role: 'form' }, [
      m(fieldnetTree, {
        selectedPoint: selectedPoint,
        ignoreRouteSelection: !(cell && cell.pointRid)
      })
    ]);
  };
  modal.vm.buttons([
    {
      label: 'Cancel',
      class: 'dib mr2',
      func: modal.vm.close
    },
    {
      label: 'Save',
      class: 'btn-good dib',
      func: submit
    }
  ]);
  modal.vm.open();
}

function openBindingEditor(ctrl, record, cell) {
  ctrl.eventErrors = [];

  return ensureEventBindingDataset(record)
    .then(function (datasetRecord) {
      return openEventPointPicker(ctrl, record, datasetRecord, cell || null);
    })
    .catch(function (error) {
      ctrl.eventErrors = [error && error.error ? error.error : (error && error.message ? error.message : 'Could not open the event point picker.')];
      m.redraw();
    });
}

function deleteBinding(ctrl, cell) {
  ctrl.eventErrors = [];
  if (!cell || !cell.columnRecord || !cell.columnRecord['@rid']) {
    return;
  }

  return columnForm.deleteDialogue(cell.columnRecord);
}

function openLocationPicker(ctrl, record) {
  ctrl.eventErrors = [];

  var currentSelection = ctrl.eventState && ctrl.eventState.locationSelection
    ? ctrl.eventState.locationSelection()
    : {};
  var modalSelection = prop(currentSelection || {});

  function submit() {
    var nextSelection = modalSelection() || {};
    var previousRid = normalizeRid(currentSelection && currentSelection['@rid']);
    var nextRid = normalizeRid(nextSelection && nextSelection['@rid']);

    if (nextRid === previousRid) {
      modal.vm.close();
      return Promise.resolve();
    }

    ctrl.eventState.locationSelection(nextSelection);
    loadLocationSummary(ctrl, nextRid);
    setDirty(ctrl);

    return queueSaveEventConfig(ctrl)
      .then(function () {
        modal.vm.close();
        m.redraw();
      })
      .catch(function () {});
  }

  modal.vm.title('Choose Location');
  modal.vm.body = function () {
    return m('form', { role: 'form' }, [
      m('div.f5.mid-gray.mb3', 'Pick the location this report event should be associated with.'),
      m(treeLocations, { selector: modalSelection })
    ]);
  };
  modal.vm.buttons([
    {
      label: 'Cancel',
      class: 'dib mr2',
      func: modal.vm.close
    },
    {
      label: 'Save',
      class: 'btn-good dib',
      func: submit
    }
  ]);
  modal.vm.open();
}

function maybeSyncBindingChanges(ctrl, record) {
  var eventConfig = getEventConfig(record);
  var hasLocalEnable = ctrl.eventState && ctrl.eventState.enabled;
  var currentBindingSignature = bindingSignature(buildBindingsPayload(record));
  var storedBindingSignature = bindingSignature(eventConfig.bindings);

  if (!hasLocalEnable && !eventConfig.enabled) {
    ctrl._bindingSyncAttemptSignature = null;
    return;
  }

  if (currentBindingSignature === storedBindingSignature) {
    ctrl._bindingSyncAttemptSignature = null;
    return;
  }

  if (ctrl.eventSaveInFlight || ctrl._bindingSyncAttemptSignature === currentBindingSignature) {
    return;
  }

  ctrl._bindingSyncAttemptSignature = currentBindingSignature;
  queueSaveEventConfig(ctrl).catch(function () {});
}

function renderLocationField(ctrl, record, locationLabel, selectedLocation, selectedLocationRid) {
  var hasLocation = ctrl.eventLocationPathLabel || (selectedLocation && selectedLocation.name) || selectedLocationRid;

  return m('div.mh3.mt2.mb3', [
    m('div.w-100', [
      m('div.f5.fw6.mb2', 'Location'),
      m('div.ba.b--moon-gray.br2.ph2.pv2', {
        style: {
          minHeight: '3rem',
          display: 'flex',
          alignItems: 'center'
        }
      }, [
        m('button.w-100.tl.pointer.pa0.bn.bg-transparent', {
          type: 'button',
          style: {
            appearance: 'none'
          },
          onclick: function () {
            openLocationPicker(ctrl, record);
          }
        }, [
          m('span.f5' + (hasLocation ? '.blue' : '.moon-gray'), locationLabel)
        ])
      ])
    ])
  ]);
}

var component = {};

component.oninit = function (vnode) {
  var ctrl = this;
  var record = vnode.attrs && vnode.attrs.record;
  ctrl.showLocation = !(vnode.attrs && vnode.attrs.showLocation === false);
  ctrl.eventErrors = [];
  ctrl.eventSaveInFlight = false;
  ctrl.eventStateDirty = false;
  ctrl.eventLocationRid = null;
  ctrl.eventLocationPathLabel = '';
  ctrl.eventLocationLoading = false;
  ctrl.eventPointPathLabels = Object.create(null);
  ctrl.eventState = createEventState(ctrl, record || {});
  ctrl._eventSaveSequence = Promise.resolve();
  ctrl._bindingSyncAttemptSignature = null;
  ctrl._eventStateRid = record && record['@rid'];
  ctrl._eventStateSignature = buildStateSignature(record || {});
};

component.view = function (vnode) {
  var ctrl = this;
  var record = vnode.attrs && vnode.attrs.record;
  if (!record || !record['@rid']) return null;

  ctrl.showLocation = !(vnode.attrs && vnode.attrs.showLocation === false);
  ctrl._activeRecord = record;
  syncEventState(ctrl, record);
  maybeSyncBindingChanges(ctrl, record);

  var cells = eventBindings.buildEventBindingCells(record);
  var selectedLocation = ctrl.eventState.locationSelection() || {};
  var selectedLocationRid = getSelectedLocationRid(ctrl, record);
  var locationLabel = ctrl.eventLocationPathLabel
    || (selectedLocation && selectedLocation.name)
    || (ctrl.eventLocationLoading ? 'Loading location…' : '')
    || (selectedLocationRid || 'Choose a location');
  var expanded = Boolean(ctrl.eventState.enabled);
  var containerClass = 'div.cf.mh3.bw1.mt2.mb4.b--grey' + (expanded ? '.pv3.bt.pl2.bl' : '.pa3.ba');

  return m('div', [
    vnode.attrs && vnode.attrs.showLocation === false
      ? null
      : renderLocationField(ctrl, record, locationLabel, selectedLocation, selectedLocationRid),
    m(containerClass, {
      style: {
        'border-left-style': expanded ? 'dotted' : 'solid'
      }
    }, [
      m('div.v-btm.flex.justify-between.items-center.flex-wrap.f2', [
        m('div.flex.items-center.overflow-hidden', [
          m('div.white', 'Event Reporting')
        ]),
        m('div.flex.items-center.flex-wrap', [
          m(toggleSwitch, {
            checked: ctrl.eventState.enabled,
            ariaLabel: 'Enable report event tracking',
            onchange: function (ev) {
              ctrl.eventState.enabled = Boolean(ev.target.checked);
              setDirty(ctrl);
              queueSaveEventConfig(ctrl).catch(function () {});
            }
          })
        ])
      ]),
      m('div' + (expanded ? '' : '.dn'), [
        ctrl.eventErrors && ctrl.eventErrors.length ? m('div.mt2', m(flash, { items: ctrl.eventErrors, bg: 'bg-red' })) : null,
        ctrl.eventSaveInFlight ? m('div.f6.moon-gray.mt2', 'Saving changes…') : null,
        m('div.mt3', [
          m('div.f5.fw6.mb2', 'Event Points'),
          m('table.w-100.collapse.f6', [
            m('tbody', cells.map(function (cell) {
              return m('tr.bb.b--near-black', { key: cell.columnRid || cell.pointRid || cell.columnId }, [
                m('td.ph2.pv2.f5.pointer.blue', {
                  onclick: function () {
                    return openBindingEditor(ctrl, record, cell);
                  }
                }, ensurePointPathLabel(ctrl, cell.pointRid, cell.pointName)),
                m('td.ph2.pv2.tr', [
                  m('div.flex.justify-end', [
                    m('div.ba.bw1.ph2.pv1.tc.v-btm.pointer', {
                      onclick: function () {
                        return deleteBinding(ctrl, cell);
                      }
                    }, [
                      m('i.f4.ion-ios-trash.red')
                    ])
                  ])
                ])
              ]);
            }).concat([
              m('tr.bb.b--near-black.pointer', {
                key: 'add-event-point-row',
                onclick: function () {
                  openBindingEditor(ctrl, record, null);
                }
              }, [
                m('td.ph2.pv2.blue.f5', [
                  m('i.ion-plus.mr2'),
                  'Add Event Point'
                ]),
                m('td.ph2.pv2')
              ])
            ]))
          ])
        ])
      ])
    ])
  ]);
};

module.exports = component;
